import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from '@/auth/interfaces';
import { EstadosOrdenTrabajoEnumChoice, ROUTER_PATHS } from '@/shared';
import { useFetchOrdenTrabajos } from '@/shared/actions/erp';
import { GeneralTable, TableTabsScene } from '@/shared/components/ui';
import { TABLE_CONSTANTS } from '@/shared/constants/ui';
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from "@/shared/hooks";
import { useTableFilter } from "@/shared/hooks/useTableFilter";
import { OrdenTrabajo } from "@/shared/interfaces";
import { hasAllPermissions } from "@/shared/utils";
import { useColumnsInstalacionesOT } from "../../../shared/hooks";
import { useUiStore } from "@/store/ui";
import { toast } from "react-toastify";
import { Button } from "@mui/material";

export type InstalacionesAsignadasTecnicoPageProps = {
  editPath?: string;
  stateOT?: EstadosOrdenTrabajoEnumChoice;
  stateOT_exclude?: EstadosOrdenTrabajoEnumChoice;
  editIcon?: React.ReactNode;
  all_ot?: boolean;
  tipo_orden_servicio?: string
  // Excluir por tipo de servicio (ej. sacar TRASLADO de la lista de instalaciones).
  tipo_orden_servicio_exclude?: string

  hora_liberacion?: number | null
  max_dia_liberacion?: number | null
  min_dia_liberacion?: number | null
  filtro_horario_instalacion?: boolean
};
const returnUrlInstalacionesTecnicoPage =
  ROUTER_PATHS.tecnico.ordenenTrabajoNav;

const redirectATS = ROUTER_PATHS.tecnico.atsNav;

const InstalacionesAsignadasTecnicoPage: React.FC<
  InstalacionesAsignadasTecnicoPageProps
> = ({
  editPath = `${returnUrlInstalacionesTecnicoPage}/asignadas`,
  stateOT = EstadosOrdenTrabajoEnumChoice.EN_ESPERA,
  stateOT_exclude = undefined,
  all_ot = false,
  editIcon = null,
  tipo_orden_servicio,
  tipo_orden_servicio_exclude,
  hora_liberacion,
  max_dia_liberacion = null,
  min_dia_liberacion = null,
  filtro_horario_instalacion = false,
}) => {
    useCheckPermissions(PermissionsEnum.api_view_ordentrabajo);
    const navigate = useNavigate();
    const setConfirmDialog = useUiStore((s) => s.setConfirmDialog);
    const setConfirmDialogIsOpen = useUiStore((s) => s.setConfirmDialogIsOpen);
    // const setAlert = useUiStore(s => s.setAlert)

    // server side filters - colums table
    const { filterObject, columnFilters, setColumnFilters } =
      useTableServerSideFiltering();

    ///* table
    const {
      globalFilter,
      pagination,
      searchTerm,
      onChangeFilter,
      setPagination,
      onChangeRangeDate,
      cleanFilter,
    } = useTableFilter();
    const { pageIndex, pageSize } = pagination;

    ///* fetch data
    const {
      data: OrdensTrabajoPagingRes,
      isLoading,
      isRefetching,
    } = useFetchOrdenTrabajos({
      enabled: true,
      params: {
        page: pageIndex + 1,
        page_size: pageSize,
        identificacion: searchTerm,
        hora_liberacion: hora_liberacion,
        max_dia_liberacion: max_dia_liberacion,
        min_dia_liberacion: min_dia_liberacion,
        filtro_horario_instalacion: filtro_horario_instalacion,
        ...filterObject,
        filterByState: false,
        estado_orden_trabajo: stateOT_exclude || all_ot ? undefined : stateOT,
        estado_orden_trabajo_exclude: all_ot ? undefined : stateOT_exclude,
        tipo_orden_servicio: tipo_orden_servicio,
        tipo_orden_servicio_exclude: tipo_orden_servicio_exclude,
      },
    });


    ///* columns
    const {
      //con diccionario
      asiganadasTecnicoColumnas,
      //sin diccionario
      // instalacionesAsignadasTecnicoColumns,
      instalacionesAsignadasRecoordinadasTecnicoColumns,
      instalacionesRealizadasColumns,
      instalacionesPendienteActualizarColumns,
      columnsVisitaTecnico,
    } = useColumnsInstalacionesOT();

    return (
      <TableTabsScene
        globalFilter={globalFilter}
        onChangeFilter={onChangeFilter}
        searchInputText="por identificación"
        onChangeRangeDate={onChangeRangeDate}
        cleanFilter={cleanFilter}
      >
        <GeneralTable<OrdenTrabajo>
          columns={
            stateOT === EstadosOrdenTrabajoEnumChoice.EN_ESPERA
              ? tipo_orden_servicio === 'VISITA'
                ? columnsVisitaTecnico
                : asiganadasTecnicoColumnas
              : stateOT === EstadosOrdenTrabajoEnumChoice.ESPERA_RECOORDINADA
                ? instalacionesAsignadasRecoordinadasTecnicoColumns
                : stateOT === EstadosOrdenTrabajoEnumChoice.REALIZADA
                  || stateOT === EstadosOrdenTrabajoEnumChoice.REALIZADA_ACTUALIZADA
                  || stateOT === EstadosOrdenTrabajoEnumChoice.REALIZADA_APROBADA
                  ? instalacionesRealizadasColumns
                  : stateOT === EstadosOrdenTrabajoEnumChoice.ESPERA_ACTUALIZAR ?
                    instalacionesPendienteActualizarColumns
                    : []
          }
          data={OrdensTrabajoPagingRes?.data?.data || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          // // filters - server side
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          // // search
          enableGlobalFilter={false}
          // // pagination
          pagination={pagination}
          onPaging={setPagination}
          rowCount={OrdensTrabajoPagingRes?.data?.count}
          // // actions ========
          enableColumnActions={hasAllPermissions([
            PermissionsEnum.api_change_ordentrabajo,
          ])}
          actionsColumnSize={60}
          // crud
          canEdit={hasAllPermissions([PermissionsEnum.api_change_ordentrabajo])}
          onEdit={(original: OrdenTrabajo) => {
            /* if (hasPermission(PermissionsEnum.revision_contabilidad_tecnico)) {
              toast.info(`La orden de trabajo se encuentra en el estado: ${original?.estado_orden_trabajo || "Desconocido"}`, {
                autoClose: 6000
              });
              navigate(`${editPath}/${original.id_orden_trabajo}`);
              return;
            } */
            if (!!original?.ats_data && !original?.ats_data?.completado) {
              setConfirmDialog({
                isOpen: true,
                title: "ATS no completado",
                subtitle: "Debe completar el análisis de trabajo seguro para llenar la orden de trabajo. ¿Desea continuar?",
                onConfirm: () => {
                  navigate(`${redirectATS}${original.ats_data?.id_analisis_trabajo_seguro!}/`);
                  setConfirmDialogIsOpen(false);
                },
                onClose() {
                  setConfirmDialogIsOpen(false);
                },
              });
            } else if (!!original?.ats_data && original?.ats_data?.completado) {
              /* if(hasPermission(PermissionsEnum.revision_contabilidad_tecnico)){
                toast.info(`La orden de trabajo se encuentra en el estado:${original.estado_orden_trabajo || "Desconocido"}`,{
                  autoClose:600
                })
              } */
              navigate(`${editPath}/${original.id_orden_trabajo}`)
              /* } else if (!!original?.ats_data && original?.ats_data?.completado) {
                onEdit(original) */
            } else {
              return toast.error("Análisis de trabajo seguro (ATS) no disponible.")
            }
          }
          }
          editIcon={
            stateOT === EstadosOrdenTrabajoEnumChoice.REALIZADA || stateOT_exclude || all_ot ? editIcon : null
          }
          toolTipTitleEditIcon={
            stateOT === EstadosOrdenTrabajoEnumChoice.REALIZADA
              ? 'Ver detalle'
              : 'Editar'
          }
          canDelete={false}
        />
      </TableTabsScene>
    );
  };

export default InstalacionesAsignadasTecnicoPage;
